import { Button } from "@/Components";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { X } from "react-feather";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { LoaderCircle } from "@/Icons";

export const RecommendationGuideContext = createContext();

export function RecommendationGuideProvider({ children }) {
  const [data, setData] = useState({});

  const onFormChange = (e) => {
    const { name, value } = e.target;
    setData({ ...data, [name]: value });
  };

  const setStep = (step) => {
    setData({
      ...data,
      step,
    });
  };

  return (
    <RecommendationGuideContext.Provider
      value={{ data, setData, onFormChange, setStep }}
    >
      {children}
    </RecommendationGuideContext.Provider>
  );
}

export function RecommendationGuide() {
  return (
    <RecommendationGuideProvider>
      <GuideLayout />
    </RecommendationGuideProvider>
  );
}

function GuideLayout() {
  const { data, onFormChange, setStep } = useContext(
    RecommendationGuideContext,
  );

  const [isFormValid, setIsFormValid] = useState(false);

  const showAsDialog = data.step > 0;

  const steps = usePage().props.guide_steps;

  return (
    <div className="relative flex justify-center gap-5 bg-white px-10 py-10 max-lg:mt-10 max-md:flex-col max-md:gap-0 lg:px-40 lg:py-20">
      {!showAsDialog && (
        <section className="relative z-10 flex max-w-[580px] flex-col max-md:w-full">
          <form
            className="flex grow flex-col self-stretch"
            onSubmit={(e) => {
              e.preventDefault();
              setStep(2);
            }}
          >
            <h2 className="font-rozhaone text-4xl lg:text-[44px]">
              Get your recommendation
            </h2>
            <p className="mt-8 text-lg leading-7 ">{steps?.step1?.question}</p>
            <div className="mt-9 flex flex-wrap gap-4">
              {steps?.step1?.options.map((option) => (
                <RadioOption
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  name={steps?.step1?.name}
                  checked={data?.[steps?.step1?.name] === option.value}
                  required
                  onChange={(e) => {
                    onFormChange({
                      target: {
                        name: steps?.step1?.name,
                        value: option.value,
                      },
                    });
                    setIsFormValid(e.target.form.checkValidity());
                  }}
                />
              ))}
            </div>
            <Button disabled={!isFormValid} className="mt-8 self-center">
              Next question
            </Button>
          </form>
        </section>
      )}

      {showAsDialog && (
        <div className="flex items-center justify-center max-sm:min-h-[490px] sm:min-h-[305px]">
          <LoaderCircle size={40} />
        </div>
      )}

      <Dialog openModal={showAsDialog} closeModal={() => setStep(null)}>
        {data.step >= 0 && data.step <= 9 && (
          <Step
            stepData={steps[`step${data.step}`]}
            stepNumber={data.step}
            totalSteps={9}
          />
        )}
      </Dialog>

      <img
        loading="lazy"
        src="/images/decorative-polygon-darkblue.svg"
        alt=""
        role="presentation"
        className="pointer-events-none absolute left-0 top-12 w-[145px] max-w-full max-md:opacity-50"
      />

      <img
        loading="lazy"
        src="/images/decorative-polygon-lightblue.svg"
        alt=""
        role="presentation"
        className="pointer-events-none absolute bottom-12 right-0 w-[145px] max-w-full max-md:opacity-50"
      />
    </div>
  );
}

function RadioOption({ label, value, name, onChange = (_) => null, ...props }) {
  return (
    <label
      htmlFor={`${name}_${value}`}
      className="flex w-full cursor-pointer items-center gap-4 rounded-sm border border-dashed border-gray-darker bg-white px-8 py-4 text-md leading-tight outline-4 outline-[rgba(31,68,68,.10)] has-[:checked]:outline max-md:px-4 sm:basis-1/2-gap-4"
    >
      <input
        id={`${name}_${value}`}
        type="radio"
        name={name}
        onChange={onChange}
        {...props}
      />
      {label}
    </label>
  );
}

function CheckboxOption({
  label,
  value,
  name,
  onChange = (_) => null,
  ...props
}) {
  return (
    <label
      htmlFor={`${name}_${value}`}
      className="mb-2 flex w-full cursor-pointer items-center rounded-sm text-md leading-tight"
    >
      <input
        id={`${name}_${value}`}
        type="checkbox"
        name={name}
        onChange={onChange}
        className="mr-2"
        {...props}
      />
      {label}
    </label>
  );
}

function Dialog({ openModal, closeModal, children }) {
  const ref = useRef();

  useEffect(() => {
    if (openModal) {
      ref.current?.showModal();
    } else {
      ref.current?.close();
    }
  }, [openModal]);

  return (
    <dialog
      ref={ref}
      onCancel={closeModal}
      className="w-full max-w-[1182px] max-xl:max-w-[calc(100%-2rem)]"
    >
      <div className="relative flex justify-center gap-5 bg-white px-10 py-10 max-md:flex-col max-md:gap-0 lg:px-40 lg:py-40">
        <button
          className="absolute right-4 top-4 lg:right-10 lg:top-10"
          onClick={closeModal}
          aria-label="Close recommendation guide"
        >
          <X size={24} aria-hidden="true" />
        </button>

        {children}

        <img
          loading="lazy"
          src="/images/decorative-polygon-darkblue.svg"
          alt=""
          role="presentation"
          className="pointer-events-none absolute left-0 top-12 w-[145px] max-w-full max-md:opacity-50"
        />

        <img
          loading="lazy"
          src="/images/decorative-polygon-lightblue.svg"
          alt=""
          role="presentation"
          className="pointer-events-none absolute bottom-12 right-0 w-[145px] max-w-full max-md:opacity-50"
        />
      </div>
    </dialog>
  );
}

function Step({ stepData, stepNumber, totalSteps }) {
  const { data, onFormChange, setStep } = useContext(
    RecommendationGuideContext,
  );

  const [isFormValid, setIsFormValid] = useState(false);

  useEffect(() => {
    const fieldData = data?.[stepData?.name] || null;
    const validated =
      (fieldData !== null && !Array.isArray(fieldData)) ||
      (Array.isArray(fieldData) && fieldData?.length > 0);

    setIsFormValid(validated);
  }, [stepNumber]);

  if (!stepData) return null;

  const isCheckbox = stepData.option_type === "checkbox";

  /**
   * Comment/Uncomment below to disable/enable auto-focus on the first input element
   */
  // useEffect(() => {
  //   document
  //     .getElementById(`${stepData.name}_${stepData.options[0].value}`)
  //     ?.focus();
  // }, [stepNumber]);

  return (
    <section className="z-10 flex w-full max-w-[580px] flex-col max-sm:min-h-[490px] sm:min-h-[305px]">
      <form
        className="flex grow flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          if (stepNumber === totalSteps) {
            Inertia.visit(route("recommendation.result"), {
              data: data,
            });
          } else {
            setStep(stepNumber + 1);
          }
        }}
      >
        <p className="text-balance text-2xl font-medium leading-7">
          {stepData.question}
        </p>
        <div
          className={`mt-9 gap-4 ${isCheckbox ? "md:columns-2" : "flex flex-wrap"}`}
        >
          {stepData.options.map((option) => {
            if (isCheckbox) {
              return (
                <CheckboxOption
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  name={stepData.name}
                  required={!data?.[stepData.name]}
                  checked={
                    data?.[stepData.name]?.includes(option.value) || false
                  }
                  onChange={(e) => {
                    const { name } = e.target;
                    const currentValues = data?.[name] || [];
                    const newValues = currentValues.includes(option.value)
                      ? currentValues.filter((v) => v !== option.value)
                      : [...currentValues, option.value];
                    onFormChange({
                      target: {
                        name,
                        value: newValues,
                      },
                    });
                    setIsFormValid(newValues.length > 0);
                  }}
                />
              );
            } else {
              return (
                <RadioOption
                  key={option.value}
                  label={option.label}
                  value={option.value}
                  name={stepData.name}
                  checked={data?.[stepData.name] === option.value}
                  required
                  onChange={(e) => {
                    onFormChange({
                      target: {
                        name: stepData.name,
                        value: option.value,
                      },
                    });
                    setIsFormValid(e.target.form.checkValidity());
                  }}
                />
              );
            }
          })}
        </div>
        <div className="grow"></div>
        <div className="mt-16 flex flex-wrap items-center justify-between max-md:mt-10">
          <Button
            className={`self-center ${stepNumber === 1 ? "invisible" : ""}`}
            showArrowLeft={true}
            showArrowRight={false}
            theme="white"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              setStep(stepNumber - 1);
            }}
          >
            Back
          </Button>
          <p className="text-center text-sm font-medium text-gray-inactive max-md:order-last max-md:mt-4 max-md:w-full">{`${stepNumber} of ${totalSteps}`}</p>
          {stepNumber < totalSteps && (
            <Button disabled={!isFormValid} className="self-center">
              Next <span className="max-md:hidden">question</span>
            </Button>
          )}
          {stepNumber === totalSteps && (
            <Button disabled={!isFormValid} className="self-center">
              See result
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}
